import React from 'react';
import PropTypes from 'prop-types';
import removeInvalidHtmlChars from 'common/App/removeInvalidHtmlChars';
import BeneficialOwner from 'components/WhdStatement/beneficialOwner';
import SimpleAutoComplete from 'components/Common/Inputs/SimpleAutoComplete';

const StatementStructureTab = (props) => {
  const {
    // helpers
    buildElementId,
    getChapter3DescriptionByName,
    getChapter4DescriptionByName,
    getIsDirectBeneficialOwner,

    // data/context
    statement,
    structure,
    settings,
    permissions,
    accountId,
    versionNumber,

    // state/flags
    isTierExpanded,
    isAllTiersExpanded,
    isPooled,
    showingVersion,
    isSavingStatement,
    isDeletingRow,
    isStructureValid,
    isAddingNewBeneficalOwner,
    canUploadPermission,
    canEditPermission,
    tierRecipients,
    tierIntermediaries,

    // collections / filters
    selectedPills,
    validationResult,
    collapseStructure,
    ownerList,

    // callbacks
    toggleCollapseTier,
    tierHasValidationErrors,
    openAddFormModal,
    addBeneficialOwner,
    isJointAccount,
    openChangeParentModal,
    openAccountWithholdingModal,
    updateToggledCollapsibleTiers,
    openPooledRatesModal,
    buildImyTierObject,
    dispatch,
    scrollToItem,
    togglePill,
    getPillClass,
    toggleCollapseAllTiers,

    // computed flags
    missingForm,
    formInReview,
    formComplete,
    invalidForm,
    hasRejections,
    hasWarnings,
    missingFormsCount,
    invalidFormsCount,
    inReviewFormsCount,
    rejectionsCount,
    warningsCount,
    missingAllocationCount,

    // optional alias
    withholdingStruct,
  } = props;

  const localWithholdingStruct = withholdingStruct || structure;

  return (
    <div id={ buildElementId(statement.intermediaryName) } className="col-12 pt-2 beneficiary-owner">
      <div className="columns">
        <div className="col-3 mt-3rem mb-2rem">
          <SimpleAutoComplete
            options={ ownerList }
            onClickAction={ scrollToItem }
            placeholder="Search Statement"
          />
        </div>
        <div className="col-9 mt-3rem mb-2rem">
          <div className="filter-container">
            <strong className="ml-1rem mr-1rem">Forms:</strong>
            <button
              type="button"
              disabled={ missingFormsCount === 0 }
              className={ `status-pill red ${ getPillClass(missingFormsCount, 'missingForms') }` }
              onClick={ () => togglePill('missingForms') }
            >
              {missingFormsCount} Missing
            </button>
            <button
              type="button"
              disabled={ invalidFormsCount === 0 }
              className={ `status-pill red ${ getPillClass(invalidFormsCount, 'invalidForms') }` }
              onClick={ () => togglePill('invalidForms') }
            >
              {invalidFormsCount} Invalid
            </button>
            <button
              type="button"
              disabled={ inReviewFormsCount === 0 }
              className={ `status-pill orange ${ getPillClass(inReviewFormsCount, 'inReviewForms') }` }
              onClick={ () => togglePill('inReviewForms') }
            >
              {inReviewFormsCount} In Review
            </button>
            <strong className="ml-1rem mr-1rem">Statement:</strong>
            <button
              type="button"
              disabled={ rejectionsCount === 0 }
              className={ `status-pill red ${ getPillClass(rejectionsCount, 'rejections') }` }
              onClick={ () => togglePill('rejections') }
            >
              {rejectionsCount} Rejection(s)
            </button>
            <button
              type="button"
              disabled={ warningsCount === 0 }
              className={ `status-pill orange ${ getPillClass(warningsCount, 'warnings') }` }
              onClick={ () => togglePill('warnings') }
            >
              {warningsCount} Warning(s)
            </button>
            <button
              type="button"
              disabled={ missingAllocationCount === 0 }
              className={ `status-pill orange ${ getPillClass(missingAllocationCount, 'missingAllocation') }` }
              onClick={ () => togglePill('missingAllocation') }
            >
              {missingAllocationCount} Allocation % Missing
            </button>
            <button
              type="button"
              onClick={ toggleCollapseAllTiers }
              className="btn"
              data-testid={ isAllTiersExpanded ? 'btn-collapse-all-tiers' : 'btn-open-all-tiers' }
            ><span className="icon icon-resize-vert" /> { isAllTiersExpanded ? 'Collapse' : 'Open' } All Tiers</button>
          </div>
        </div>
      </div>
      <div className="statement-breadcrumbs">
        <div className="breadcrumbs">/ { statement.intermediaryName }</div>
        <div className="button-row">
          <button type="button" onClick={ toggleCollapseTier } className="btn link-button">
            <span className="icon icon-resize-vert" />
            { isTierExpanded ? 'Collapse' : 'Open' } Tier
          </button>
        </div>
      </div>
      <div className={ `panel mb-2 imy-card${ (tierHasValidationErrors() && ' validation-highlight') || '' }` } data-testid={ removeInvalidHtmlChars(`${ statement.intermediaryName }`).toLowerCase() }>
        <div className="columns">
          <div className="col-12">
            <div className="columns imy-layer">
              <div className="col-6 align-vertical-middle">
                <div className="owner-header">
                  <h2 className="inline mr-2rem value" id={ `${ buildElementId(statement.intermediaryName) }_bo-header` }>{ statement.intermediaryName }</h2>
                  { missingForm && (<span className="status-pill red flat">Form Missing</span>) }
                  { formInReview && (<span className="status-pill orange flat">Form In Review</span>) }
                  { formComplete && (<span className="status-pill grey flat">Form Complete</span>) }
                  { structure?.basicDetails?.formId != null && (
                    invalidForm ? (
                      <span className="status-pill red flat">Form Invalid</span>
                    ) : (
                      <span className="status-pill grey flat">Form Valid</span>
                    )
                  ) }
                  { hasRejections > 0 && (<button type="button" className="status-pill red flat">Rejection(s)</button>) }
                  { hasWarnings > 0 && (<button type="button" className="status-pill orange flat">Warning(s)</button>) }
                </div>

                <div className="owner-header value mr-2">{ tierRecipients } { isPooled ? 'Pool' : 'Owner' }{ tierRecipients === 0 || tierRecipients > 1 ? 's' : '' }</div>
                { !isPooled && <div className="owner-header value">/ { tierIntermediaries } Intermediar{ tierIntermediaries === 0 || tierIntermediaries > 1 ? 'ies' : 'y' }</div> }
              </div>
              <div className="col-2" />
              <div className="col-4 align-vertical-center">
                {!settings.selectedClient.isPiiProtectionEnabled && !isPooled && canUploadPermission && !showingVersion && (
                  <div className="col-4 align-vertical-center">
                    <button
                      type="button"
                      className="btn btn-secondary"
                      onClick={ () => openAddFormModal(buildImyTierObject(localWithholdingStruct.basicDetails)) }
                      data-testid="btn-upload-forms"
                    >
                      <i className="icon icon-2x icon-upload mr-2" />Upload Form(s)
                    </button>
                  </div>
                )}
                { canEditPermission && !showingVersion && (
                  <div className="col-4 align-vertical-center">
                    <button
                      type="button"
                      className="btn btn-secondary"
                      disabled={ isAddingNewBeneficalOwner }
                      onClick={ () => addBeneficialOwner(accountId, null, false, isPooled, false, statement.intermediaryName) }
                      data-testid={ isPooled ? 'btn-add-pool' : 'btn-add-beneficial-owner' }
                    >
                      <i className="icon icon-2x icon-plus mr-2" />{ isPooled ? 'Add Pool' : 'Add Owner' }
                    </button>
                  </div>
                )}
                { !isPooled && canEditPermission && !showingVersion && (
                  <div className="col-4 align-vertical-center">
                    <button
                      type="button"
                      className="btn btn-secondary"
                      disabled={ isAddingNewBeneficalOwner }
                      onClick={ () => addBeneficialOwner(accountId, null, true, isPooled, true, statement.intermediaryName) }
                      data-testid="btn-add-imy-tier"
                    >
                      <i className="icon icon-2x icon-plus mr-2" />Add IMY Tier
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
      { structure.recipients && structure.recipients.map((x, idx) => (
        <BeneficialOwner
          key={ x.guid || (x.ownerDetails && x.ownerDetails.guid) }
          beneficialOwner={ x }
          selectedPills={ selectedPills }
          decimalPrecision={ statement.decimalPlacePrecision ?? 8 }
          getChapter3DescriptionByName={ getChapter3DescriptionByName }
          getChapter4DescriptionByName={ getChapter4DescriptionByName }
          parents={ [statement.intermediaryName] }
          statementType={ statement.type }
          isJointAccount={ isJointAccount() }
          isTopTierExpanded={ isAllTiersExpanded }
          isParentTierExpanded={ isTierExpanded }
          openChangeParentModal={ openChangeParentModal }
          idx={ idx }
          dispatch={ dispatch }
          permissions={ permissions }
          openAccountWithholdingModal={ formId => openAccountWithholdingModal(formId) }
          openAddFormModal={ node => openAddFormModal(node) }
          addBeneficialOwner={ (id, parentGuid, isImyLayer, preventExpand) => addBeneficialOwner(id, parentGuid, isImyLayer, isPooled, preventExpand, parentGuid) }
          topAccountId={ accountId }
          isSavingStatement={ isSavingStatement }
          updateToggledCollapsibleTiers={ (rowIdentifier, toggleState) => updateToggledCollapsibleTiers(rowIdentifier, toggleState) }
          tierCollapseStructure={ collapseStructure }
          validationResult={ validationResult }
          validationLineNo={ (x.ownerDetails || x).lineNo }
          openPooledRatesModal={ (guid, rates) => openPooledRatesModal(guid, rates) }
          parentAccountId={ accountId }
          isDeletingRow={ isDeletingRow }
          isStructureValid={ isStructureValid }
          isAddingNewBeneficalOwner={ isAddingNewBeneficalOwner }
          statementVersion={ versionNumber }
          parentTierTotalAllocation={ 100 }
          hideTierAllocationValue={ getIsDirectBeneficialOwner(x) }
        />
      )) }
    </div>
  );
};

export default StatementStructureTab;

StatementStructureTab.propTypes = {
  buildElementId: PropTypes.func.isRequired,
  getChapter3DescriptionByName: PropTypes.func.isRequired,
  getChapter4DescriptionByName: PropTypes.func.isRequired,
  getIsDirectBeneficialOwner: PropTypes.func.isRequired,
  statement: PropTypes.shape({
    intermediaryName: PropTypes.string,
    decimalPlacePrecision: PropTypes.number,
    type: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
  }).isRequired,
  structure: PropTypes.shape({
    basicDetails: PropTypes.shape({
      formId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      accountId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      name: PropTypes.string,
      formIsValid: PropTypes.bool,
      workflowStage: PropTypes.number,
    }),
    recipients: PropTypes.arrayOf(PropTypes.shape({})),
  }).isRequired,
  settings: PropTypes.shape({
    selectedClient: PropTypes.shape({ isPiiProtectionEnabled: PropTypes.bool })
  }).isRequired,
  permissions: PropTypes.arrayOf(PropTypes.string),
  accountId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  versionNumber: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  isTierExpanded: PropTypes.bool,
  isAllTiersExpanded: PropTypes.bool,
  isPooled: PropTypes.bool,
  showingVersion: PropTypes.bool,
  isSavingStatement: PropTypes.bool,
  isDeletingRow: PropTypes.bool,
  isStructureValid: PropTypes.bool,
  isAddingNewBeneficalOwner: PropTypes.bool,
  canUploadPermission: PropTypes.bool,
  canEditPermission: PropTypes.bool,
  tierRecipients: PropTypes.number,
  tierIntermediaries: PropTypes.number,
  selectedPills: PropTypes.shape({
    missingForms: PropTypes.bool,
    invalidForms: PropTypes.bool,
    inReviewForms: PropTypes.bool,
    rejections: PropTypes.bool,
    warnings: PropTypes.bool,
    missingAllocation: PropTypes.bool,
  }),
  validationResult: PropTypes.shape({
    lines: PropTypes.arrayOf(PropTypes.shape({})),
    rejections: PropTypes.arrayOf(PropTypes.shape({})),
    warnings: PropTypes.arrayOf(PropTypes.shape({})),
  }),
  collapseStructure: PropTypes.shape({}),
  toggleCollapseTier: PropTypes.func.isRequired,
  tierHasValidationErrors: PropTypes.func.isRequired,
  openAddFormModal: PropTypes.func.isRequired,
  addBeneficialOwner: PropTypes.func.isRequired,
  isJointAccount: PropTypes.func.isRequired,
  openChangeParentModal: PropTypes.func.isRequired,
  openAccountWithholdingModal: PropTypes.func.isRequired,
  updateToggledCollapsibleTiers: PropTypes.func.isRequired,
  openPooledRatesModal: PropTypes.func.isRequired,
  buildImyTierObject: PropTypes.func.isRequired,
  dispatch: PropTypes.func.isRequired,
  withholdingStruct: PropTypes.shape({
    basicDetails: PropTypes.shape({})
  }),
  missingForm: PropTypes.bool,
  formInReview: PropTypes.bool,
  formComplete: PropTypes.bool,
  invalidForm: PropTypes.bool,
  hasRejections: PropTypes.bool,
  hasWarnings: PropTypes.bool,
  ownerList: PropTypes.arrayOf(PropTypes.shape({})),
  scrollToItem: PropTypes.func,
  togglePill: PropTypes.func,
  getPillClass: PropTypes.func,
  toggleCollapseAllTiers: PropTypes.func,
  missingFormsCount: PropTypes.number,
  invalidFormsCount: PropTypes.number,
  inReviewFormsCount: PropTypes.number,
  missingAllocationCount: PropTypes.number,
  rejectionsCount: PropTypes.number,
  warningsCount: PropTypes.number,
};

StatementStructureTab.defaultProps = {
  permissions: [],
  versionNumber: undefined,
  isTierExpanded: false,
  isAllTiersExpanded: false,
  isPooled: false,
  showingVersion: false,
  isSavingStatement: false,
  isDeletingRow: false,
  isStructureValid: false,
  isAddingNewBeneficalOwner: false,
  canUploadPermission: false,
  canEditPermission: false,
  tierRecipients: 0,
  tierIntermediaries: 0,
  selectedPills: {
    missingForms: false,
    invalidForms: false,
    inReviewForms: false,
    rejections: false,
    warnings: false,
    missingAllocation: false,
  },
  validationResult: { lines: [], rejections: [], warnings: [] },
  collapseStructure: {},
  withholdingStruct: null,
  missingForm: false,
  formInReview: false,
  formComplete: false,
  invalidForm: false,
  hasRejections: false,
  hasWarnings: false,
  ownerList: [],
  scrollToItem: () => {},
  togglePill: () => {},
  getPillClass: () => '',
  toggleCollapseAllTiers: () => {},
  missingFormsCount: 0,
  invalidFormsCount: 0,
  inReviewFormsCount: 0,
  missingAllocationCount: 0,
  rejectionsCount: 0,
  warningsCount: 0,
};
